import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { surveysApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { ClipboardList, Star, Plus, Loader2, BarChart3, Send } from 'lucide-react';

export default function SurveysPage() {
  const { currentProperty } = useAuth();
  const pid = currentProperty?.id;
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [respondFor, setRespondFor] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    if (!pid) return;
    try { const r = await surveysApi.list(pid); setSurveys(r.data || []); }
    catch (e) { toast.error('Failed to load surveys'); }
    finally { setLoading(false); }
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const openResults = async (s) => { try { const r = await surveysApi.results(s.id); setResults(r.data); } catch (e) { toast.error('Failed'); } };

  if (!currentProperty) return <div className="p-6"><Card><CardContent className="p-8 text-center text-slate-500">Select a property.</CardContent></Card></div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-pink-600 text-white flex items-center justify-center"><ClipboardList className="h-6 w-6" /></div>
          <div><h1 className="text-2xl font-bold text-slate-900">Satisfaction Surveys</h1><p className="text-sm text-slate-500">Collect and analyze customer feedback</p></div>
        </div>
        <Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4 mr-2" /> New Survey</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading ? <Card><CardContent className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></CardContent></Card>
          : surveys.length === 0 ? <Card><CardContent className="p-8 text-center text-slate-500">No surveys yet.</CardContent></Card>
          : surveys.map((s) => (
            <Card key={s.id}>
              <CardHeader><CardTitle className="text-base">{s.title}</CardTitle><CardDescription>{s.description}</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">{(s.questions || []).length} questions</span>
                  <Badge variant="secondary">{s.response_count} responses</Badge>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openResults(s)}><BarChart3 className="h-4 w-4 mr-1" /> Results</Button>
                  <Button size="sm" onClick={() => setRespondFor(s)}><Send className="h-4 w-4 mr-1" /> Respond</Button>
                </div>
              </CardContent>
            </Card>
          ))}
      </div>

      <ResultsDialog results={results} onClose={() => setResults(null)} />
      <RespondDialog survey={respondFor} onClose={() => setRespondFor(null)} onSaved={() => { setRespondFor(null); load(); }} />
      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} pid={pid} onSaved={() => { setCreateOpen(false); load(); }} />
    </div>
  );
}

function Stars({ value }) {
  const v = Math.round(value || 0);
  return <span className="inline-flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= v ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />)}</span>;
}

function ResultsDialog({ results, onClose }) {
  const r = results;
  return (
    <Dialog open={!!r} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{r?.survey?.title} — Results</DialogTitle><DialogDescription>{r?.response_count} responses</DialogDescription></DialogHeader>
        {r && (
          <div className="space-y-4">
            <div className="rounded-lg bg-amber-50 p-4 text-center">
              <p className="text-xs text-slate-500">Overall Satisfaction</p>
              <p className="text-3xl font-bold text-amber-600">{r.overall_average ?? '—'}<span className="text-base text-slate-400">/5</span></p>
              <Stars value={r.overall_average} />
            </div>
            {(r.questions || []).map((q) => (
              <div key={q.id} className="border-b pb-2">
                <p className="text-sm font-medium">{q.text}</p>
                {q.type === 'RATING' ? (
                  <div className="flex items-center gap-2"><Stars value={q.average} /><span className="text-sm text-slate-600">{q.average ?? '—'} ({q.count})</span></div>
                ) : (
                  <ul className="text-xs text-slate-600 list-disc pl-4 mt-1">{(q.answers || []).slice(0, 8).map((t, i) => <li key={i}>{t}</li>)}{(q.answers || []).length === 0 && <li className="list-none text-slate-400">No answers</li>}</ul>
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RespondDialog({ survey, onClose, onSaved }) {
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (survey) { setAnswers({}); setName(''); } }, [survey]);
  const setA = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));
  const save = async () => {
    try { setSaving(true); await surveysApi.submitResponse(survey.id, { respondent_name: name || null, answers }); toast.success('Thank you — response recorded'); onSaved(); }
    catch (e) { toast.error('Failed'); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!survey} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{survey?.title}</DialogTitle><DialogDescription>{survey?.description}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Your name (optional)</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
          {(survey?.questions || []).map((q) => (
            <div key={q.id} className="space-y-1.5">
              <Label>{q.text}</Label>
              {q.type === 'RATING' ? (
                <div className="flex gap-1">{[1, 2, 3, 4, 5].map((i) => (
                  <button key={i} type="button" onClick={() => setA(q.id, i)}><Star className={`h-7 w-7 ${i <= (answers[q.id] || 0) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} /></button>
                ))}</div>
              ) : (
                <Input value={answers[q.id] || ''} onChange={(e) => setA(q.id, e.target.value)} placeholder="Your answer" />
              )}
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button><Button onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Submit</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({ open, onOpenChange, pid, onSaved }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState([{ id: 'q1', text: '', type: 'RATING' }]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setTitle(''); setDescription(''); setQuestions([{ id: 'q1', text: '', type: 'RATING' }]); } }, [open]);
  const setQ = (i, k, v) => setQuestions(questions.map((q, idx) => (idx === i ? { ...q, [k]: v } : q)));
  const addQ = () => setQuestions([...questions, { id: `q${questions.length + 1}`, text: '', type: 'RATING' }]);
  const save = async () => {
    if (!title.trim()) { toast.error('Enter a title'); return; }
    const qs = questions.filter((q) => q.text.trim()).map((q, i) => ({ id: `q${i + 1}`, text: q.text, type: q.type }));
    if (qs.length === 0) { toast.error('Add at least one question'); return; }
    try { setSaving(true); await surveysApi.create(pid, { title, description, questions: qs }); toast.success('Survey created'); onSaved(); }
    catch (e) { toast.error('Failed'); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Survey</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Description</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          <div className="space-y-2">
            <div className="flex items-center justify-between"><Label>Questions</Label><Button type="button" size="sm" variant="outline" onClick={addQ}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button></div>
            {questions.map((q, i) => (
              <div key={i} className="flex gap-2">
                <Input value={q.text} onChange={(e) => setQ(i, 'text', e.target.value)} placeholder={`Question ${i + 1}`} />
                <select value={q.type} onChange={(e) => setQ(i, 'type', e.target.value)} className="border rounded px-2 text-sm">
                  <option value="RATING">Rating</option><option value="TEXT">Text</option>
                </select>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button><Button onClick={save} disabled={saving}>Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
